import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { useState, useEffect } from 'react';
import axiosInstance, { endpoints } from 'src/utils/axios';
import { UserSearchAutocompleteType } from 'src/type/search';

export type UserOption = {
  label: string;
  value: string;
};

type Props = {
  onChange: (user: UserOption | null) => void;
  value: UserOption | null;
  label?: string;
};

export default function UserSearchAutocomplete({ onChange, value, label }: Props) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<readonly UserOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (text.length < 1) {
      setOptions([]);
      setOpen(false);
      return () => {};
    }

    const debounceTimer = setTimeout(() => {
      setOpen(true);

      (async () => {
        try {
          setLoading(true);

          const { data } = await axiosInstance.get<UserSearchAutocompleteType>(
            `${endpoints.users.search}?text=${text}`
          );

          const users: UserOption[] = data?.hits?.map((user) => ({
            label: `${user.firstName} ${user.lastName}`,
            value: user.objectID,
          }));

          setOptions([...users]);
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      })();
    }, 300); // 300ms debounce delay

    // Cleanup function to clear the timer if text changes before 300ms
    return () => clearTimeout(debounceTimer);
  }, [text]);

  const handleClose = () => {
    setOpen(false);
    setOptions([]);
  };

  const handleSelect = (user: UserOption | null) => {
    onChange(user);
    setText('');
    setOpen(false);
  };

  return (
    <Autocomplete
      fullWidth
      open={open}
      // onOpen={handleOpen}
      value={value}
      onClose={handleClose}
      isOptionEqualToValue={(option, v) => option.value === v.value}
      getOptionLabel={(option) => option.label}
      options={options}
      loading={loading}
      onChange={(event, newValue) => {
        handleSelect(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setText(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || 'ค้นหาด้วยชื่อ'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
